import { Swiper, Navigation } from '../vendor/swiper.js';

/** Phones and tablets: homepage rows use the browser scroller. Swiper from 1024px. */
const HOME_NATIVE_MQ = '(max-width: 1023px)';
const NATIVE_DRAG_THRESHOLD = 8;

/** @type {WeakMap<HTMLElement, () => void>} */
const nativeHomeCleanups = new WeakMap();
/** @type {Set<() => void>} */
const nativeRefreshers = new Set();
let homeNativeMqBound = false;

function bindHomeNativeBreakpoint() {
    if (homeNativeMqBound) {
        return;
    }

    homeNativeMqBound = true;

    const mq = window.matchMedia(HOME_NATIVE_MQ);
    const onChange = () => {
        initProductsSliders(document);
        nativeRefreshers.forEach((fn) => fn());
    };

    if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', onChange);
    } else {
        mq.addListener(onChange);
    }
}

export function prefersNativeHomeScroll() {
    return window.matchMedia(HOME_NATIVE_MQ).matches;
}

/**
 * @param {() => void} fn
 */
export function onNativeCarouselBreakpoint(fn) {
    nativeRefreshers.add(fn);
    bindHomeNativeBreakpoint();
}

/**
 * @param {HTMLElement} el
 */
function isHomepageTouchCarousel(el) {
    return Boolean(
        el.closest('.catalog-hits-slider, .home-events, .promote-block, .bottom_slider, .about-hits'),
    );
}

/**
 * Slide edges inside the native track. `max` stops at the last card,
 * so a page button cannot open the empty tail past that card.
 *
 * @param {HTMLElement} el
 * @returns {{ offsets: number[], max: number }}
 */
function nativeTrackMax(el) {
    const wrapper = el.querySelector(
        ':scope > .swiper-wrapper, :scope > .slides.swiper-wrapper, :scope > ul.swiper-wrapper',
    );
    const scrollMax = Math.max(0, el.scrollWidth - el.clientWidth);

    if (!(wrapper instanceof HTMLElement)) {
        return { offsets: [], max: scrollMax };
    }

    const slides = [...wrapper.children].filter(
        (node) => node instanceof HTMLElement && node.offsetWidth > 0,
    );

    if (!slides.length) {
        return { offsets: [], max: scrollMax };
    }

    const offsets = slides.map((slide) => slide.offsetLeft);
    const last = slides[slides.length - 1];
    const contentMax = Math.max(0, last.offsetLeft + last.offsetWidth - el.clientWidth);

    return {
        offsets,
        max: Math.min(scrollMax, contentMax),
    };
}

/**
 * One card per click. A full-viewport step moves every bouquet on screen
 * (two on a phone). The last click stops flush with the last card.
 *
 * @param {number[]} offsets
 * @param {number} max
 * @param {number} current
 * @param {number} direction 1 or -1
 * @returns {number}
 */
function nativePageScrollLeft(offsets, max, current, direction) {
    /** @type {number[]} */
    const edges = [0, max];

    offsets.forEach((offset) => {
        if (offset >= -1 && offset <= max + 1) {
            edges.push(Math.max(0, Math.min(max, offset)));
        }
    });

    if (direction > 0) {
        return nearestEdge(edges, current, 1, max);
    }

    return nearestEdge(edges, current, -1, 0);
}

/**
 * Closest card edge strictly ahead of or behind the current offset.
 *
 * @param {number[]} edges
 * @param {number} current
 * @param {number} direction 1 or -1
 * @param {number} fallback
 * @returns {number}
 */
function nearestEdge(edges, current, direction, fallback) {
    let nearest = /** @type {number | null} */ (null);

    edges.forEach((edge) => {
        if (direction > 0 && edge <= current + 1) {
            return;
        }
        if (direction < 0 && edge >= current - 1) {
            return;
        }
        if (nearest === null || (direction > 0 ? edge < nearest : edge > nearest)) {
            nearest = edge;
        }
    });

    return nearest === null ? fallback : nearest;
}

/**
 * @param {HTMLElement} el
 * @param {HTMLButtonElement} prevEl
 * @param {HTMLButtonElement} nextEl
 */
function syncNativeHomeNav(el, prevEl, nextEl) {
    const { max } = nativeTrackMax(el);
    const locked = max <= 1;
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft >= max - 1;

    prevEl.classList.toggle('swiper-button-lock', locked);
    nextEl.classList.toggle('swiper-button-lock', locked);
    prevEl.classList.toggle('swiper-button-disabled', locked || atStart);
    nextEl.classList.toggle('swiper-button-disabled', locked || atEnd);
}

/**
 * Mouse drag only. Touch stays on the browser scroller.
 *
 * @param {HTMLElement} el
 * @returns {() => void}
 */
function attachMouseDragScroll(el) {
    let pointerId = null;
    let axis = /** @type {null | 'x' | 'y'} */ (null);
    let startX = 0;
    let startY = 0;
    let startScroll = 0;
    let dragged = false;
    let clickBlocker = null;

    const clearClickBlocker = () => {
        if (!clickBlocker) {
            return;
        }
        el.removeEventListener('click', clickBlocker, true);
        clickBlocker = null;
    };

    const onPointerDown = (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) {
            return;
        }
        clearClickBlocker();
        pointerId = event.pointerId;
        axis = null;
        dragged = false;
        startX = event.clientX;
        startY = event.clientY;
        startScroll = el.scrollLeft;
    };

    const onPointerMove = (event) => {
        if (pointerId === null || event.pointerId !== pointerId) {
            return;
        }

        const dx = event.clientX - startX;
        const dy = event.clientY - startY;

        if (axis === null) {
            if (Math.abs(dx) < NATIVE_DRAG_THRESHOLD && Math.abs(dy) < NATIVE_DRAG_THRESHOLD) {
                return;
            }
            axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
            if (axis === 'x') {
                el.setPointerCapture(event.pointerId);
            }
        }

        if (axis !== 'x') {
            return;
        }

        dragged = true;
        event.preventDefault();
        el.scrollLeft = startScroll - dx;
    };

    const onPointerUp = (event) => {
        if (pointerId === null || event.pointerId !== pointerId) {
            return;
        }
        if (dragged) {
            clearClickBlocker();
            clickBlocker = (clickEvent) => {
                clickEvent.preventDefault();
                clickEvent.stopPropagation();
                clearClickBlocker();
            };
            el.addEventListener('click', clickBlocker, true);
        }
        pointerId = null;
        axis = null;
        dragged = false;
    };

    const onPointerCancel = (event) => {
        if (pointerId === null || event.pointerId !== pointerId) {
            return;
        }
        pointerId = null;
        axis = null;
        dragged = false;
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove, { passive: false });
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerCancel);

    return () => {
        clearClickBlocker();
        el.removeEventListener('pointerdown', onPointerDown);
        el.removeEventListener('pointermove', onPointerMove);
        el.removeEventListener('pointerup', onPointerUp);
        el.removeEventListener('pointercancel', onPointerCancel);
    };
}

/**
 * @param {HTMLElement} el
 */
export function enableNativeHomeScroll(el) {
    const wrapper = el.querySelector(
        ':scope > .swiper-wrapper, :scope > .slides.swiper-wrapper, :scope > ul.swiper-wrapper',
    );

    if (!(wrapper instanceof HTMLElement)) {
        return;
    }

    wrapper.classList.add('swiper-wrapper');
    el.classList.add('swiper', 'products-swiper', 'is-native-scroll');

    const { prevEl, nextEl } = setupProductsSwiperNavigation(el);

    if (el.dataset.nativeScroll === '1') {
        syncNativeHomeNav(el, prevEl, nextEl);
        return;
    }

    Array.from(wrapper.children).forEach((slide) => {
        if (slide instanceof HTMLElement) {
            slide.classList.add('swiper-slide');
        }
    });

    const onScroll = () => syncNativeHomeNav(el, prevEl, nextEl);
    const scrollPage = (direction) => {
        const view = el.clientWidth;
        if (view <= 0) {
            return;
        }
        const { offsets, max } = nativeTrackMax(el);
        const left = nativePageScrollLeft(offsets, max, el.scrollLeft, direction);
        el.scrollTo({ left, behavior: 'smooth' });
    };
    const onPrev = (event) => {
        event.preventDefault();
        scrollPage(-1);
    };
    const onNext = (event) => {
        event.preventDefault();
        scrollPage(1);
    };

    prevEl.addEventListener('click', onPrev);
    nextEl.addEventListener('click', onNext);
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    const dragCleanup = attachMouseDragScroll(el);

    nativeHomeCleanups.set(el, () => {
        prevEl.removeEventListener('click', onPrev);
        nextEl.removeEventListener('click', onNext);
        el.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        dragCleanup();
        prevEl.classList.remove('swiper-button-disabled', 'swiper-button-lock');
        nextEl.classList.remove('swiper-button-disabled', 'swiper-button-lock');
    });

    el.dataset.nativeScroll = '1';
    markSwiperReady(el);
    onScroll();
    window.requestAnimationFrame(onScroll);
}

/**
 * @param {HTMLElement} el
 */
export function disableNativeHomeScroll(el) {
    const cleanup = nativeHomeCleanups.get(el);

    if (cleanup) {
        cleanup();
        nativeHomeCleanups.delete(el);
    }

    if (el.dataset.nativeScroll !== '1' && !el.classList.contains('is-native-scroll')) {
        return;
    }

    el.classList.remove('is-native-scroll');
    delete el.dataset.nativeScroll;
    el.scrollLeft = 0;
}

/**
 * Product card carousels: promote-block + bottom_slider (ex-flexslider / Siema).
 * Default grid: slidesPerView 2.08 / 3.7@768 / 5.5@1280.
 * Blog (.blog-linked-products): 2.08 / 3@768 / 3|4@992 via data-slides-desktop.
 * About-hits (`.about-hits`): slidesPerView auto — CSS 2 / 3.2@768 / 292px@992,
 * wide advertised slide is 2 columns; card ratio matches catalog-hits (180:258, 1:1.21 from 576).
 * Home catalog hits (`.catalog-hits-slider`): integer 2 / 3@768 / 4@992, no peek.
 * Below 1024px native scroll: catalog-hits, home-events, promote, PDP «Другие букеты», about-hits.
 * Desktop keeps Swiper.
 * Markup: [data-products-swiper].swiper > .swiper-wrapper > .swiper-slide
 *
 * Navigation buttons are created automatically and placed outside
 * the swiper overflow container.
 *
 * @param {ParentNode} [root=document]
 * @returns {import('swiper').default[]}
 */
export function initProductsSliders(root = document) {
    const instances = [];

    bindHomeNativeBreakpoint();

    const elements = root.querySelectorAll('[data-products-swiper]');

    elements.forEach((el) => {
        if (!(el instanceof HTMLElement)) {
            return;
        }

        if (isHomepageTouchCarousel(el)) {
            if (prefersNativeHomeScroll()) {
                if (el.swiper && !el.swiper.destroyed) {
                    el.swiper.destroy(true, true);
                    delete el.dataset.swiperInited;
                }
                enableNativeHomeScroll(el);
                return;
            }

            disableNativeHomeScroll(el);
        }

        // Already initialized — refresh layout (e.g. after tab show)
        if (el.dataset.swiperInited === '1' && el.swiper) {
            el.swiper.update();
            instances.push(el.swiper);
            return;
        }

        const wrapper = el.querySelector(
            ':scope > .swiper-wrapper, :scope > .slides.swiper-wrapper, :scope > ul.swiper-wrapper',
        );

        if (!wrapper) {
            return;
        }

        wrapper.classList.add('swiper-wrapper');

        el.classList.add(
            'swiper',
            'products-swiper',
            'products-swiper--loading',
        );

        /*
         * Wrap swiper in shell and create navigation buttons outside
         * the element with overflow: hidden.
         */
        const { prevEl, nextEl } = setupProductsSwiperNavigation(el);

        Array.from(wrapper.children).forEach((slide) => {
            if (slide instanceof HTMLElement) {
                slide.classList.add('swiper-slide');
            }
        });

        const isAboutHits = Boolean(el.closest('.about-hits'));
        const isCatalogHitsSlider = Boolean(el.closest('.catalog-hits-slider'));
        const isHomeEvents = Boolean(el.closest('.home-events'));
        const isEmptyBasketHits = Boolean(el.closest('.order-empty-hits'));

        /** @type {import('swiper').SwiperOptions} */
        const options = {
            modules: [Navigation],

            slidesPerView: isAboutHits ? 'auto' : 2.08,
            spaceBetween: 10,

            watchOverflow: true,
            observer: true,
            observeParents: true,

            loop: false,

            breakpoints: isAboutHits
                ? {
                      768: {
                          slidesPerView: 'auto',
                          spaceBetween: 10,
                      },
                      992: {
                          slidesPerView: 'auto',
                          spaceBetween: 10,
                      },
                  }
                : {
                      768: {
                          slidesPerView: 3.7,
                          spaceBetween: 10,
                      },
                      1280: {
                          slidesPerView: 5.5,
                          spaceBetween: 10,
                      },
                  },

            pagination: false,

            navigation: {
                prevEl,
                nextEl,
            },

            on: {
                init(swiper) {
                    markSwiperReady(swiper.el);
                },
                // Swiper's auto + spaceBetween grid drifts off the real card edges.
                // Prev then fails to find the current snap and jumps to the start,
                // and the old post-transition correction nudged the last slide.
                slidesUpdated(swiper) {
                    if (isAboutHits) {
                        syncAboutHitsSnapGrid(swiper);
                    }
                },
            },
        };

        // Blog only: replace default PDP/promote grid. Mobile peek 2.08; 4@992 when --wide, else 3.
        const blogHost =
            el.closest('.blog-linked-products') ||
            (root instanceof Element ? root.closest('.blog-linked-products') : null);
        if (isEmptyBasketHits) {
            options.slidesPerView = 'auto';
            options.breakpoints = {};
        } else if (isCatalogHitsSlider) {
            options.slidesPerView = 2;
            options.spaceBetween = 0;
            options.breakpoints = {
                768: {
                    slidesPerView: 3,
                },
                992: {
                    slidesPerView: 4,
                },
            };
        } else if (isHomeEvents) {
            options.slidesPerView = 2.06;
            options.spaceBetween = 0;
            options.breakpoints = {
                768: {
                    slidesPerView: 3,
                },
                992: {
                    slidesPerView: 4,
                },
            };
        } else if (blogHost instanceof HTMLElement) {
            let desktopSpv = Number.parseInt(
                blogHost.dataset.slidesDesktop || '',
                10,
            );
            if (!Number.isFinite(desktopSpv) || desktopSpv <= 0) {
                desktopSpv = 3;
            }
            options.slidesPerView = 2.08;
            options.breakpoints = {
                768: {
                    slidesPerView: 3,
                    spaceBetween: 10,
                },
                992: {
                    slidesPerView: desktopSpv,
                    spaceBetween: 10,
                },
            };
        }

        const instance = new Swiper(el, options);

        if (isAboutHits) {
            instance.slideNext = (speed, runCallbacks, internal) =>
                slideAboutHitsBy(instance, 1, speed, runCallbacks, internal);
            instance.slidePrev = (speed, runCallbacks, internal) =>
                slideAboutHitsBy(instance, -1, speed, runCallbacks, internal);
        }

        el.dataset.swiperInited = '1';

        markSwiperReady(el);

        // Recalc after layout (tab may have been display:none / width settling)
        window.requestAnimationFrame(() => {
            instance.update();
        });

        instances.push(instance);
    });

    observeBigdataInject(root);

    return instances;
}

/**
 * One snap per click, from the nearest card edge at or behind the current offset.
 * Swiper's own prev looks the translate up in the snap list and, on a miss, goes to 0.
 *
 * @param {import('swiper').default} swiper
 * @param {number} direction 1 or -1
 * @param {number} [speed]
 * @param {boolean} [runCallbacks]
 * @param {boolean} [internal]
 * @returns {boolean}
 */
function slideAboutHitsBy(swiper, direction, speed, runCallbacks, internal) {
    const grid = swiper.snapGrid || [];

    if (!grid.length) {
        return swiper.slideTo(0, speed, runCallbacks, internal);
    }

    const current = Math.round(-(swiper.translate || 0));
    let index = 0;

    grid.forEach((pos, i) => {
        if (pos <= current + 1) {
            index = i;
        }
    });

    const next = Math.max(0, Math.min(grid.length - 1, index + direction));

    return swiper.slideTo(next, speed, runCallbacks, internal);
}

/**
 * Snap points are each card's real left edge, same as a homepage row:
 * one arrow click moves one slide, and the last click stops flush
 * with the end instead of correcting afterwards.
 *
 * @param {import('swiper').default} swiper
 */
function syncAboutHitsSnapGrid(swiper) {
    if (!swiper || swiper.destroyed) {
        return;
    }

    const slides = swiper.slides;

    if (!slides?.length) {
        return;
    }

    const last = slides[slides.length - 1];

    if (!(last instanceof HTMLElement)) {
        return;
    }

    const view = swiper.size || swiper.el?.clientWidth || 0;

    if (view <= 0) {
        return;
    }

    const limit = Math.max(0, Math.round(last.offsetLeft + last.offsetWidth - view));
    /** @type {number[]} */
    const grid = [];

    slides.forEach((slide) => {
        if (!(slide instanceof HTMLElement) || slide.offsetWidth <= 0) {
            return;
        }

        const pos = Math.round(slide.offsetLeft);

        if (pos > limit + 1) {
            return;
        }

        if (grid.length === 0 || pos > grid[grid.length - 1] + 1) {
            grid.push(pos);
        }
    });

    if (!grid.length) {
        grid.push(0);
    }

    if (limit - grid[grid.length - 1] > 1) {
        grid.push(limit);
    }

    swiper.snapGrid = grid;
    swiper.slidesGrid = grid.slice();
}

/**
 * Wraps the swiper into .products-swiper-shell and creates navigation buttons.
 *
 * Result:
 *
 * .products-swiper-shell
 * ├── .products-swiper
 * ├── .swiper-button-prev
 * └── .swiper-button-next
 *
 * @param {HTMLElement} el
 * @returns {{
 *   shell: HTMLElement,
 *   prevEl: HTMLButtonElement,
 *   nextEl: HTMLButtonElement
 * }}
 */
function setupProductsSwiperNavigation(el) {
    const existingShell = el.closest('.products-swiper-shell');

    if (existingShell instanceof HTMLElement) {
        const existingPrev = existingShell.querySelector('.swiper-button-prev');
        const existingNext = existingShell.querySelector('.swiper-button-next');

        if (
            existingPrev instanceof HTMLButtonElement &&
            existingNext instanceof HTMLButtonElement
        ) {
            return {
                shell: existingShell,
                prevEl: existingPrev,
                nextEl: existingNext,
            };
        }
    }

    const shell = document.createElement('div');
    shell.className = 'products-swiper-shell';

    el.parentNode.insertBefore(shell, el);
    shell.appendChild(el);

    const prevEl = document.createElement('button');

    prevEl.type = 'button';
    prevEl.className = 'swiper-button-prev';
    prevEl.setAttribute('aria-label', 'Назад');

    prevEl.innerHTML = `
      <svg width="6" height="12" aria-hidden="true" focusable="false">
          <use href="#icon-arrow-left" xlink:href="#icon-arrow-left"></use>
      </svg>
    `;

    const nextEl = document.createElement('button');

    nextEl.type = 'button';
    nextEl.className = 'swiper-button-next';
    nextEl.setAttribute('aria-label', 'Вперёд');

    nextEl.innerHTML = `
      <svg width="6" height="12" aria-hidden="true" focusable="false">
          <use href="#icon-arrow-right" xlink:href="#icon-arrow-right"></use>
      </svg>
    `;

    shell.append(prevEl, nextEl);

    return {
        shell,
        prevEl,
        nextEl,
    };
}

/**
 * @param {unknown} host
 */
function markSwiperReady(host) {
    if (!(host instanceof HTMLElement)) {
        return;
    }

    host.classList.remove('products-swiper--loading');
    host.classList.add('products-swiper--ready');
    host.removeAttribute('aria-busy');
}

/**
 * Big Data injects HTML into #UNIQ after cloud response — re-init slider/cards.
 *
 * @param {ParentNode} root
 */
function observeBigdataInject(root) {
    const scope = root instanceof Document ? root : document;

    const hosts = scope.querySelectorAll(
        '.bottom_slider .bigdata-rcm-host, .bottom_slider [data-products-swiper]',
    );

    hosts.forEach((host) => {
        const watch = host.classList.contains('products-swiper-skeleton')
            ? host.parentElement
            : host.closest('.bottom_slider') || host;

        if (
            !(watch instanceof HTMLElement) ||
            watch.dataset.productsObserveBound === '1'
        ) {
            return;
        }

        watch.dataset.productsObserveBound = '1';

        const observer = new MutationObserver(() => {
            window.setTimeout(() => {
                initProductsSliders(watch);

                if (
                    typeof window.ProstocvetInitProductCards === 'function'
                ) {
                    window.ProstocvetInitProductCards(watch);
                }
            }, 30);
        });

        observer.observe(watch, {
            childList: true,
            subtree: true,
        });
    });
}
